"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
export default function TheArtOfTheEdge() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cityPlannerPrinciples = [{
    number: "01",
    title: "Precision Mowing Protocols",
    description: "We implement strict height-of-cut standards tailored to your specific turf species for optimal health and aesthetic uniformity."
  }, {
    number: "02",
    title: "Vertical Edge Engineering",
    description: "Our edging creates a permanent architectural boundary between turf and hardscape, defined by a clean 90-degree vertical cut."
  }, {
    number: "03",
    title: "Seasonal Growth Analysis",
    description: "We adjust our maintenance frequency based on real-time growth data, ensuring your lawn never loses its engineered silhouette."
  }, {
    number: "04",
    title: "Debris-Free Execution",
    description: "Every session concludes with high-velocity clearing of all clippings from walkways, leaving only the precision of the blade behind."
  }, {
    number: "05",
    title: "Long-Term Turf Stewardship",
    description: "Our multi-year care frameworks focus on soil health and root density to maintain the structural integrity of your green space."
  }];
  const privateClientPrinciples = [{
    number: "01",
    title: "Initial Site Assessment",
    description: "We begin with a technical survey of your property's topography and turf health to establish a baseline for precision care."
  }, {
    number: "02",
    title: "Custom Maintenance Cycles",
    description: "Your service schedule is engineered around your lifestyle, ensuring maximum privacy and a pristine lawn for every occasion."
  }, {
    number: "03",
    title: "Discreet Service Delivery",
    description: "Our technicians operate with surgical efficiency, respecting the tranquility of your home while delivering professional results."
  }, {
    number: "04",
    title: "Technical Detail Reports",
    description: "Receive digital updates on soil moisture, nutrient levels, and blade health after every visit for total transparency."
  }, {
    number: "05",
    title: "The Acosta Guarantee",
    description: "We treat every blade of grass as a structural element, providing ongoing refinement to ensure your landscape's perfection."
  }];
  return <section data-section-id="5069" id="the-art-of-the-edge" className="dark bg-background text-foreground py-40 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0 items-stretch">
          {}
          <div ref={imageRef} className={`relative min-h-[420px] lg:min-h-[720px] transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/precision_lawn_edging_detail.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/precision_lawn_edging_detail.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/precision_lawn_edging_detail.png-wvc-sizes" alt="Close up of a perfectly edged lawn border against a concrete walkway" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover lg:rounded-md" />
          </div>

          {}
          <div className="px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-24 flex flex-col justify-center">
            {}
            <div ref={headingRef} className={`transition-all duration-1000 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs sm:text-sm font-mono uppercase tracking-[0.2em] mb-8" style={{
              color: "var(--primary)"
            }}>
                — [MOW.01] THE SPEC
              </p>

              <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.9] font-extrabold text-foreground tracking-tighter">
                The Art of{" "}
                <span className="italic font-bold" style={{
                color: "var(--primary)"
              }}>
                  The Edge
                </span>
              </h2>

              <div className="mt-10 h-px w-24" style={{
              backgroundColor: "var(--primary)"
            }} />

              <p className="mt-10 font-default text-lg sm:text-xl leading-relaxed text-foreground/85" style={{
              maxWidth: "42ch"
            }}>
                At Acosta's Landscaping, we view your lawn as a canvas for precision engineering. We don't just cut grass; we define the boundaries of your outdoor architecture with surgical accuracy.
              </p>
            </div>

            {}
            <div ref={contentRef} className={`mt-14 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Tabs defaultValue="planners" className="w-full">
                <TabsList className="bg-transparent p-0 h-auto gap-3 flex flex-wrap">
                  <TabsTrigger value="planners" className="font-mono uppercase text-xs sm:text-sm px-6 py-2.5 rounded-md border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md transition-all">
                    Commercial Standards
                  </TabsTrigger>
                  <TabsTrigger value="private" className="font-mono uppercase text-xs sm:text-sm px-6 py-2.5 rounded-md border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md transition-all">
                    Residential Precision
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="planners" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {cityPlannerPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-mono text-sm font-medium tracking-wider pt-1.5 shrink-0" style={{
                      color: "var(--primary)"
                    }}>
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-muted-foreground max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>

                <TabsContent value="private" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {privateClientPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-mono text-sm font-medium tracking-wider pt-1.5 shrink-0" style={{
                      color: "var(--primary)"
                    }}>
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-muted-foreground max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>
              </Tabs>

              {}
              <p className="mt-14 font-mono text-sm sm:text-base text-foreground/85">
                <Link to="/GetQuotePage" className="underline underline-offset-[6px] decoration-2 transition-colors hover:text-primary" style={{
                color: "var(--primary)",
                textDecorationColor: "var(--primary)"
              }}>
                  Request a technical site assessment
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}